import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  Portal,
  Tooltip,
  useDisclosure,
  useToast,
  Input,
  Popover,
  PopoverContent,
  PopoverBody,
  PopoverAnchor,
  HStack,
} from '@chakra-ui/react';
import {
  faArrows,
  faCheck,
  faChevronDown,
  faCopy,
  faEdit,
  faPen,
  faPlus,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';

import { useFlag } from '@unleash/proxy-client-react';
import { isNull } from 'lodash';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import ConfirmDeletionModal from '../common/ConfirmDeletionModal';
import { DbIdsPerModel, useViewer } from '../common/ForgeViewer';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  Panel,
  PanelActionButtons,
  PanelContent,
  PanelHeader,
  PanelTitle,
} from '../common/Panel';
import { useViewerMode, ViewerMode } from '../common/viewer-mode';
import PlaceholderText from '../text/PlaceholderText';
import { useProjectHistory } from '../project/project-history/hooks/useProjectHistory';
import { useDiffing } from '../project/project-history/hooks/useDiffing';
import { useSheetViewerV2ContextMenuContext } from '../viewers/SheetsViewerV2/SheetsViewerV2.context';
import { ENABLE_APS_SHEET_VIEWER_FLAG } from '../viewers/SheetsViewerV2/SheetsViewerV2.config';
import ShapeItem, { ShapeItemDiff, ShapeNodeType } from './ShapeItem';
import { AutoshapePopover } from './AutoshapePopover';
import ShapesFolder from './ShapesFolder';
import { useEnabledSheetViewer } from 'src/components/viewers/common/hooks/useEnabledSheetViewer';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import {
  AutoShapeDeepFragment,
  DeleteShapeDocument,
  DeleteSheetShapeDocument,
  EventResource,
  EventType,
  GetProjectSettingsDocument,
  ShapeDeepFragment,
  ShapeFolderDeepFragment,
  SheetShapeDeepFragment,
  UpdateShapeDocument,
  UpdateSheetShapeDocument,
  BulkDeleteSparkelPropertiesDocument,
  GetSparkelPropertiesForProjectDocument,
  GetSparkelPropertiesForProjectQuery,
} from 'src/gql/graphql';
import { useActiveModels } from 'src/hooks/active-models';
import { useAutoShapes } from 'src/hooks/auto-shapes';
import { useBimShapes } from 'src/hooks/bim-shapes';
import { useLocalStorage } from 'src/hooks/local-storage';
import { useShapeFolders } from 'src/hooks/shape-folders';
import { useSheetShapes } from 'src/hooks/sheet-shapes';
import { useForgeContextMenuContext } from 'src/services/viewer/ForgeContextMenu';
import { useSelection } from 'src/services/viewer/selection';
import { useSheetContextMenuContext } from 'src/services/viewer/SheetsContextMenu';
import { useVisibility } from 'src/services/viewer/visibility';
import { ChakraColor } from 'src/utils/color-util';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { useEventHistory } from 'src/hooks/event-history';
import { getShapeUrn } from 'src/services/viewer/ShapesManager';
import { useUserTenant } from 'src/services/auth-info';
import { getRoofShapeFromProperties } from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
//

type ShapesPanelProps = {
  readOnly: boolean;
  projectId: string;
};

const SHAPE_ITEM_SIZE = 26;

// eslint-disable-next-line complexity
function ShapesPanel({ readOnly }: ShapesPanelProps) {
  const [, updateShapeMutation] = useMutation(UpdateShapeDocument);

  const [, updateSheetShapeMutation] = useMutation(UpdateSheetShapeDocument);

  const [, deleteShapeMutation] = useMutation(DeleteShapeDocument);

  const [, deleteSheetShapeMutation] = useMutation(DeleteSheetShapeDocument);

  const enableAutoShapes = useFlag('enable-auto-shapes');
  const enableSheetViewerV2 = useFlag(ENABLE_APS_SHEET_VIEWER_FLAG);
  const enableDrainageLine = useFlag('enable-drainage-line');

  const toast = useToast();
  const { t } = useTranslation(['common', 'project']);
  const { show, hide } = useVisibility();

  const panelRef = useRef<HTMLDivElement>(null);
  const [isRenamePopoverOpen, setIsRenamePopoverOpen] = useState(false);
  const [renameAnchorPos, setRenameAnchorPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [renameTarget, setRenameTarget] = useState<ShapeNodeType | null>(null);
  const [renameInput, setRenameInput] = useState<string>('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const openRenamePopover = useCallback(
    (node: ShapeNodeType, coords?: { x: number; y: number }) => {
      // If multiple shapes are selected, use the first selected shape's name as initial value
      const multiSelected = selectedShapesRef.current?.length > 1;
      const initialName = multiSelected
        ? selectedShapesRef.current[0]?.name ?? node.name ?? ''
        : node.name ?? '';

      setRenameTarget(node);
      setRenameInput(initialName);
      if (coords) {
        setRenameAnchorPos(coords);
      }
      setIsRenamePopoverOpen(true);
      // Focus and select will be handled after open via setTimeout to ensure render
      setTimeout(() => {
        if (renameInputRef.current) {
          renameInputRef.current.focus();
          renameInputRef.current.select();
        }
      }, 0);
    },
    []
  );

  const closeRenamePopover = useCallback(() => {
    setIsRenamePopoverOpen(false);
    setRenameTarget(null);
    setRenameInput('');
  }, []);
  const [shapesPanelHeight, setShapesPanelHeight] = useLocalStorage<
    number | undefined
  >('shapes-panel-height', undefined);
  const [isHoveredOutside, setIsHoveredOutside] = useState(false);

  const {
    shapes: { data: shapes3d },
    drawing: {
      activate: activate3dDrawing,
      isMovingShapes: isMovingShapes3D,
      setMovingShapes: setMovingShapes3D,
      isCopyingShapes: isCopyingShapes3D,
      setCopyingShapes: setCopyingShapes3D,
      deactivate: deactivate3dDrawing,
      isDrawing: isDrawing3d,
      hasResult: has3dDrawingResult,
      submit: submit3dDrawing,
      editShape: edit3dShape,
    },
  } = useBimShapes();

  const {
    shapes: { data: sheetShapes },
    drawing: {
      activate: activateSheetDrawingV1,
      isMovingShapes: isMovingShapes2D,
      setMovingShapes: setMovingShapes2D,
      isCopyingShapes: isCopyingShapes2D,
      setCopyingShapes: setCopyingShapes2D,
      deactivate: deactivateSheetDrawingV1,
      isDrawing: isDrawingSheetsV1,
      hasResult: hasSheetDrawingResult,
      submit: submitSheetDrawing,
      editShape: editSheetShape,
    },
  } = useSheetShapes();
  const { setActiveTool, drawing: sheetsViewerV2Drawing } = useSheetViewerV2();
  const { isProtan } = useUserTenant();
  const { setViewerMode, viewerMode } = useViewerMode();

  const isDrawingSheets =
    viewerMode === ViewerMode.SheetsV2 && enableSheetViewerV2
      ? sheetsViewerV2Drawing.isDrawing
      : isDrawingSheetsV1;
  const activateSheetDrawing =
    viewerMode === ViewerMode.SheetsV2 && enableSheetViewerV2
      ? () => {
          setActiveTool(ActiveTool.Drawing);
          // For Protan, default drawing mode to Drain Line if available, otherwise Point
          if (isProtan) {
            sheetsViewerV2Drawing.setDrawingMode(DrawingMode.Point);
          }
        }
      : activateSheetDrawingV1;
  const deactivateSheetDrawing =
    viewerMode === ViewerMode.SheetsV2 && enableSheetViewerV2
      ? () => setActiveTool(ActiveTool.Default)
      : deactivateSheetDrawingV1;

  const {
    autoShapes,
    createAutoShape,
    deleteAutoShape,
    renameAutoShape,
    findNextAvailableAutoShapeName,
  } = useAutoShapes();

  const {
    visibilityManager: { hiddenElements: hiddenBIMElements },
  } = useViewer();

  const {
    visibilityManager: { hiddenElements: hiddenSheetElements },
    setPageNumber,
    changeActiveSheet,
  } = useEnabledSheetViewer();

  const { projectId, orderId } = useParams();
  // Hotkeys: Symbol Detection quick actions
  useHotkeys(
    'shift+q',
    () => {
      // Trigger opening the Autoshape popover by clicking the "Add" button path
      // We simulate a click on the add button so the popover mounts, then start picking
      try {
        const addButton = document.querySelector(
          'button[aria-label="add-shape-options"]'
        ) as HTMLButtonElement | null;
        if (addButton) {
          addButton.click();
        }
      } catch {
        console.error('[AutoShapes] add-shape-options button not found');
      }
      // Ask popover to jump straight into Symbol Detection settings
      try {
        const ev = new Event('autoshape:open:symbol-detection-2d');
        window.dispatchEvent(ev);
      } catch {
        console.error(
          '[AutoShapes] autoshape:open:symbol-detection-2d not dispatched'
        );
      }
    },
    {
      enabled: enableAutoShapes && viewerMode === ViewerMode.SheetsV2,
    },
    []
  );

  useHotkeys(
    'esc',
    () => {
      try {
        window.dispatchEvent(new Event('symbol-detection:cancel'));
      } catch {
        console.error('[AutoShapes] symbol-detection:cancel not dispatched');
      }
    },
    {
      enabled: enableAutoShapes && viewerMode === ViewerMode.SheetsV2,
    },
    []
  );

  const { activeModels } = useActiveModels(projectId ?? '');

  const {
    activeSheetId,
    pagination: { pageNumber: activeSheetPageNumber },
  } = useSheetViewerV2();

  // Sparkel properties for determining RoofShapeDbId references
  const [{ data: sparkelPropsData }] = useQuery({
    query: GetSparkelPropertiesForProjectDocument,
    variables: { projectId: projectId ?? '' },
    pause: !projectId || !isProtan,
  });
  const [, bulkDeleteSparkelProps] = useMutation(
    BulkDeleteSparkelPropertiesDocument
  );

  const isSheetShapeNode = (
    node: ShapeNodeType
  ): node is SheetShapeDeepFragment => node.__typename === 'SheetShape';

  const cleanupRoofFlagsForDeletedDrainLines = useCallback(
    async (
      nodes: ShapeNodeType[],
      sparkelProps: GetSparkelPropertiesForProjectQuery | undefined,
      projId: string | undefined,
      isProtanTenant: boolean
    ) => {
      if (
        !isProtanTenant ||
        !sparkelProps?.project?.sparkelProperties ||
        !projId
      ) {
        return;
      }

      const sheetNodes = nodes.filter(isSheetShapeNode);
      const drainLineNodes = sheetNodes.filter((n) => !!n.sheetShapeSplitLine);

      for (const node of drainLineNodes) {
        const roofDbId = getRoofShapeFromProperties(
          sparkelProps,
          node.dbId,
          node.urn
        );

        if (!roofDbId) continue;

        const otherRefs = sparkelProps.project.sparkelProperties.filter(
          (prop) =>
            prop.propertySet === 'RoofShapeDbId' &&
            prop.modelUrn === node.urn &&
            prop.propertyValue === roofDbId.toString() &&
            prop.dbId !== node.dbId
        );

        if (otherRefs.length === 0) {
          await bulkDeleteSparkelProps({
            input: {
              projectId: projId,
              dbIds: [
                {
                  modelUrn: node.urn,
                  dbIds: [roofDbId],
                },
              ],
              thePropertySet: 'Is Tapered Insulation',
            },
          });
        }
      }
    },
    [bulkDeleteSparkelProps]
  );

  const [{ data: getProjectSettings }] = useQuery({
    query: GetProjectSettingsDocument,
    variables: { projectId: projectId ?? '' },
  });

  const globalOffset =
    getProjectSettings?.project?.settings?.viewerGlobalOffset ?? null;

  const hasDrawingResult = has3dDrawingResult || hasSheetDrawingResult;

  useHotkeys(
    'esc',
    () => {
      setMovingShapes3D(false);
      setCopyingShapes3D(false);
      setMovingShapes2D(false);
      setCopyingShapes2D(false);
    },
    {
      enabled:
        isMovingShapes3D ||
        isCopyingShapes3D ||
        isMovingShapes2D ||
        isCopyingShapes2D,
    }
  );

  useHotkeys(
    'shift+d',
    () => {
      viewerMode === ViewerMode.Models
        ? setCopyingShapes3D(true)
        : setCopyingShapes2D(true);
    },
    {
      enabled:
        !isCopyingShapes3D &&
        !isMovingShapes3D &&
        !isMovingShapes2D &&
        !isCopyingShapes2D,
    }
  );

  const {
    data: shapeFolders,
    moveShapesToFolder,
    removeShapesFromFolder,
    renameFolder,
    selectedFolder,
    setSelectedFolder,
    deleteFolder,
    createFolder,
    findNextAvailableFolderName,
  } = useShapeFolders();

  const {
    setSelectedDbIds,
    deselectDbIds,
    selectedDbIds,
    selectedShapes: selected3dShapes,
    selectedSheetShapes,
  } = useSelection();

  const { logEvent } = useEventHistory(projectId ?? '');
  const { diff, isHistoryDrawerOpen, referenceState } = useProjectHistory();
  const { getEventType, getAttributeDiff } = useDiffing();

  const selectedDbIdsInternalRef = useRef(selectedDbIds);

  useEffect(() => {
    selectedDbIdsInternalRef.current = selectedDbIds;
  }, [selectedDbIds]);

  const shapes = useMemo(() => {
    if (shapes3d && sheetShapes) {
      return [...shapes3d, ...sheetShapes];
    } else {
      return [];
    }
  }, [shapes3d, sheetShapes]);

  const shapesRef = useRef(shapes);
  useEffect(() => {
    shapesRef.current = shapes;
  }, [shapes]);

  const shapesWithNoFolder = useMemo(
    () =>
      shapes
        .filter((shape) => isNull(shape.folder) && isNull(shape.autoShapeId))
        .sort((a, b) => compareShapeNameWithNumber(a.name, b.name)),
    [shapes]
  );

  const selectedShapes = useMemo(
    () =>
      shapes.filter((shape) => {
        const urn = shape.urn;
        const selectedDbIds = selected3dShapes[urn] || selectedSheetShapes[urn];
        return selectedDbIds?.includes(shape.dbId);
      }),
    [shapes, selected3dShapes, selectedSheetShapes]
  );

  const selectedShapesRef = useRef(selectedShapes);
  useEffect(() => {
    selectedShapesRef.current = selectedShapes;
  }, [selectedShapes]);

  const groupShapesIntoFolder = useCallback(async () => {
    const newFolderName = findNextAvailableFolderName(t('common:shape-folder'));
    const newFolder = await createFolder(newFolderName);
    if (newFolder && selectedShapesRef.current) {
      const shapeIds: string[] = [];
      const sheetShapeIds: string[] = [];
      selectedShapesRef.current.forEach((shape) => {
        if (shape.__typename === 'Shape') {
          shapeIds.push(shape.id);
        } else if (shape.__typename === 'SheetShape') {
          sheetShapeIds.push(shape.id);
        }
      });

      await moveShapesToFolder(shapeIds, sheetShapeIds, newFolder.id);

      logEvent({
        eventName: 'Group Created',
        eventType: EventType.Created,
        resourceName: newFolder.name,
        resourceType: EventResource.ShapeFolder,
        metadata: {
          affectedResources: [...shapeIds, ...sheetShapeIds],
          details: `${[...shapeIds, ...sheetShapeIds].length} shape${
            [...shapeIds, ...sheetShapeIds].length === 1 ? '' : 's'
          } added to group ${newFolder.name}`,
        },
      });
    }
  }, [
    createFolder,
    findNextAvailableFolderName,
    logEvent,
    moveShapesToFolder,
    t,
  ]);

  const ungroupShapesFromFolder = useCallback(
    async (node: ShapeNodeType) => {
      const shapeIds: string[] = [];
      const sheetShapeIds: string[] = [];
      const shapes: ShapeNodeType[] = selectedShapesRef.current;
      shapes.push(node);
      for (const shape of shapes) {
        if (shape.__typename === 'Shape') {
          shapeIds.push(shape.id);
        } else if (shape.__typename === 'SheetShape') {
          sheetShapeIds.push(shape.id);
        }
      }
      await removeShapesFromFolder(shapeIds, sheetShapeIds);
    },
    [removeShapesFromFolder]
  );

  const [expandedFolders, setExpandedFolders] = useState<
    Record<string, boolean>
  >({});

  const {
    isOpen: showDeleteSelectedShapesModal,
    onOpen: onDeleteSelectedShapesModalOpen,
    onClose: onDeleteSelectedShapesModalClose,
  } = useDisclosure();

  const sortedFolders = useMemo(() => {
    // Perhaps we should add Autshapes as folders?
    let expandableNodes: (ShapeFolderDeepFragment | AutoShapeDeepFragment)[] =
      [];
    if (shapeFolders) {
      expandableNodes.push(...shapeFolders);
    }
    if (autoShapes) {
      expandableNodes.push(...autoShapes);
    }

    const sortedFolders = expandableNodes?.sort((a, b) =>
      compareShapeNameWithNumber(a.name, b.name)
    );
    return sortedFolders || [];
  }, [autoShapes, shapeFolders]);

  const sortedShapes = useMemo(() => {
    const sorted = shapes.sort((a, b) =>
      compareShapeNameWithNumber(a.name, b.name)
    );
    return sorted || [];
  }, [shapes]);

  const shapesIndexedByFolder = useMemo(() => {
    return new Map<string, Array<ShapeDeepFragment | SheetShapeDeepFragment>>(
      sortedFolders.map((folder) => [
        folder.id,
        shapes
          .filter(
            (shape) =>
              shape.folder === folder.id || shape.autoShapeId === folder.id
          )
          .sort((a, b) => compareShapeNameWithNumber(a.name, b.name)),
      ])
    );
  }, [sortedFolders, shapes]);

  const firstShapeWithoutFolder = useRef<VirtualItem | null>(null);

  // Add a structure that gets all the items in order
  const itemsToVirtualize = useMemo(() => {
    if (sortedFolders.length <= 0) {
      return sortedShapes;
    }

    const retVal: Array<
      | ShapeDeepFragment
      | SheetShapeDeepFragment
      | ShapeFolderDeepFragment
      | AutoShapeDeepFragment
    > = [];
    for (const folder of sortedFolders) {
      // Add the folder itself
      retVal.push(folder);
      // If expanded, add its children
      if (expandedFolders[folder.id]) {
        const folderShapes = sortedShapes.filter(
          (shape) =>
            shape.folder === folder.id || shape.autoShapeId === folder.id
        );
        retVal.push(...folderShapes);
      }
    }
    if (shapesWithNoFolder.length > 0) {
      retVal.push(...shapesWithNoFolder);
    }
    return retVal;
  }, [sortedFolders, sortedShapes, expandedFolders, shapesWithNoFolder]);

  const persistedVirtualFoldersRef = useRef(
    new Map<VirtualItem['key'], VirtualItem>()
  );

  const [virtualItems, setVirtualItems] = useState<VirtualItem[]>([]);
  const shapeVirtualizer = useVirtualizer({
    count: itemsToVirtualize.length,
    estimateSize: () => SHAPE_ITEM_SIZE,
    getItemKey: (index) => `${itemsToVirtualize[index].id}`,
    getScrollElement: () => panelRef.current,
    overscan: 30,
    onChange: (instance) => {
      setVirtualItems(instance.getVirtualItems());
    },
  });

  // since we are setting virtual items onchange above, which mostly means onscroll,
  // we have to manually listen to the other events where we might be interested in resizing and handle that manually below
  // the alternative is calling `getVirtualItems` on every render like we were doing before
  useLayoutEffect(() => {
    shapeVirtualizer.measure();
    setVirtualItems(shapeVirtualizer.getVirtualItems());
  }, [expandedFolders, shapeFolders, shapes]);

  const allShapesSorted = useMemo(() => {
    const allItems: {
      type: string;
      folder?: ShapeFolderDeepFragment | AutoShapeDeepFragment;
      shape?: ShapeDeepFragment | SheetShapeDeepFragment;
      folderId?: string;
    }[] = [];

    for (const folder of sortedFolders || []) {
      allItems.push({ type: 'folder', folder });

      if (expandedFolders[folder.id]) {
        const shapesInFolder = shapes
          .filter(
            (shape) =>
              shape.folder === folder.id || shape.autoShapeId === folder.id
          )
          .sort((a, b) => compareShapeNameWithNumber(a.name, b.name));

        for (const shape of shapesInFolder) {
          allItems.push({ type: 'shape', shape, folderId: folder.id });
        }
      }
    }

    for (const shape of shapesWithNoFolder) {
      allItems.push({ type: 'shape', shape, folderId: undefined });
    }

    return allItems; // Return all folders and shapes in the correct order
  }, [sortedFolders, expandedFolders, shapes, shapesWithNoFolder]);

  const allShapesSortedRef = useRef(allShapesSorted);

  useEffect(() => {
    allShapesSortedRef.current = allShapesSorted;
  }, [allShapesSorted]);

  const metaKeyPressRef = useRef(false);
  const shiftKeyPressRef = useRef(false);
  const lastSelectedShapeIdRef = useRef<string | null>(null);

  const toggleFolderExpansion = useCallback((folderId: string) => {
    setExpandedFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        metaKeyPressRef.current = true;
      } else if (e.shiftKey) {
        shiftKeyPressRef.current = true;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
        metaKeyPressRef.current = false;
        shiftKeyPressRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleShapeSelection = useCallback(
    (
      shape: ShapeDeepFragment | SheetShapeDeepFragment,
      newSelection: DbIdsPerModel
    ) => {
      if (!newSelection[shape.urn]) {
        newSelection[shape.urn] = [];
      }

      // If the shape is already selected, deselect it
      if (newSelection[shape.urn].includes(shape.dbId)) {
        deselectDbIds({ [shape.urn]: [shape.dbId] });
        newSelection[shape.urn] = newSelection[shape.urn].filter(
          (id) => id !== shape.dbId
        );

        if (newSelection[shape.urn].length === 0) {
          delete newSelection[shape.urn];
        }
      } else {
        newSelection[shape.urn].push(shape.dbId);
      }
    },
    [deselectDbIds]
  );

  const handleFolderSelection = useCallback(
    // eslint-disable-next-line complexity
    (folderId: string, newSelection: DbIdsPerModel) => {
      const shapesInFolder = shapesRef.current.filter(
        (s) => s.folder === folderId || s.autoShapeId === folderId
      );

      const allShapesSelected = shapesInFolder.every((shape) => {
        return (
          newSelection[shape.urn] &&
          newSelection[shape.urn].includes(shape.dbId)
        );
      });

      if (metaKeyPressRef.current) {
        if (allShapesSelected) {
          // Collect all the shapes to be deselected
          const shapesToDeselect: Record<string, number[]> = {};

          for (const shape of shapesInFolder) {
            if (newSelection[shape.urn]) {
              if (!shapesToDeselect[shape.urn]) {
                shapesToDeselect[shape.urn] = [];
              }
              shapesToDeselect[shape.urn].push(shape.dbId);

              newSelection[shape.urn] = newSelection[shape.urn].filter(
                (id) => id !== shape.dbId
              );

              if (newSelection[shape.urn].length === 0) {
                delete newSelection[shape.urn]; // Remove the key if no shapes are selected
              }
            }
          }

          // Batch deselect all shapes within the folder at once
          deselectDbIds(shapesToDeselect);
          setSelectedFolder(null); // Deselect the folder itself
        } else {
          // Select all shapes within the folder but NOT the folder itself
          for (const shape of shapesInFolder) {
            if (!newSelection[shape.urn]) {
              newSelection[shape.urn] = [];
            }
            if (!newSelection[shape.urn].includes(shape.dbId)) {
              newSelection[shape.urn].push(shape.dbId);
            }
          }
        }
      } else {
        // If meta is not pressed, we treat this as a regular folder selection
        if (shapesInFolder.some((s) => !!s.autoShapeId)) {
          setSelectedFolder(null);
        } else {
          setSelectedFolder(folderId);
        }
        for (const shape of shapesInFolder) {
          if (!newSelection[shape.urn]) {
            newSelection[shape.urn] = [];
          }
          if (!newSelection[shape.urn].includes(shape.dbId)) {
            newSelection[shape.urn].push(shape.dbId);
          }
        }
      }
    },
    [setSelectedFolder, deselectDbIds]
  );

  const handleNodeSelect = useCallback(
    // eslint-disable-next-line complexity
    (
      node:
        | ShapeDeepFragment
        | SheetShapeDeepFragment
        | ShapeFolderDeepFragment
        | AutoShapeDeepFragment
    ) => {
      const isShape =
        node.__typename === 'Shape' || node.__typename === 'SheetShape';
      const isFolder = !isShape; // If it's not a shape, it's a folder

      let newSelection = { ...selectedDbIdsInternalRef.current };

      // Handle Shift-Select (do not select the folder itself)
      if (shiftKeyPressRef.current && lastSelectedShapeIdRef.current) {
        const allItemsSorted = allShapesSortedRef.current;

        // Find indices of the last selected item and the currently clicked item
        const startIdx = allItemsSorted.findIndex(
          (item) =>
            (item.type === 'shape' &&
              item.shape?.id === lastSelectedShapeIdRef.current) ||
            (item.type === 'folder' &&
              item.folder?.id === lastSelectedShapeIdRef.current)
        );
        const endIdx = allItemsSorted.findIndex(
          (item) =>
            (item.type === 'shape' && item.shape?.id === node.id) ||
            (item.type === 'folder' && item.folder?.id === node.id)
        );

        if (startIdx !== -1 && endIdx !== -1) {
          const [minIdx, maxIdx] = [
            Math.min(startIdx, endIdx),
            Math.max(startIdx, endIdx),
          ];
          const itemsInRange = allItemsSorted.slice(minIdx, maxIdx + 1);

          for (const item of itemsInRange) {
            if (item.type === 'folder' && item.folder) {
              handleFolderSelection(item.folder.id, newSelection);
            } else if (item.type === 'shape' && item.shape) {
              if (!newSelection[item.shape.urn]) {
                newSelection[item.shape.urn] = [];
              }
              if (!newSelection[item.shape.urn].includes(item.shape.dbId)) {
                newSelection[item.shape.urn].push(item.shape.dbId);
              }
            }
          }
        }
      } else {
        // Handle regular click (no meta or shift key)
        if (!metaKeyPressRef.current) {
          newSelection = {};
          //setSelectedDbIds({});
          setSelectedFolder(null);
        }

        // For regular clicks, select the folder or shape
        if (isShape) {
          handleShapeSelection(node, newSelection);
          lastSelectedShapeIdRef.current = node.id; // Store the last clicked shape for shift-select
        } else if (isFolder) {
          handleFolderSelection(node.id, newSelection); // Select the folder and its shapes, or deselect with meta key
          lastSelectedShapeIdRef.current = node.id; // Store the last clicked folder for shift-select
        }
      }

      // Update the selection state
      setSelectedDbIds(newSelection);
    },
    [
      setSelectedDbIds,
      handleFolderSelection,
      setSelectedFolder,
      handleShapeSelection,
    ]
  );

  const handleShapeMove = useCallback(
    async (
      shapesToMove: (ShapeDeepFragment | SheetShapeDeepFragment)[],
      folder: ShapeFolderDeepFragment | null
    ) => {
      // Check if the shape is already in the folder
      const isShapeInFolder = (
        shape: ShapeDeepFragment | SheetShapeDeepFragment
      ) => shape.folder === folder?.id;

      if (shapesToMove.every(isShapeInFolder)) {
        return; // Do nothing if all shapes are already in the folder
      }

      const [shapeIdsToMove, sheetShapeIdsToMove] = shapesToMove.reduce(
        (acc, shape) => {
          if (shape.__typename === 'Shape') {
            acc[0].push(shape.id);
          } else {
            acc[1].push(shape.id);
          }
          return acc;
        },
        [[], []] as [string[], string[]]
      );

      if (folder) {
        await moveShapesToFolder(
          shapeIdsToMove,
          sheetShapeIdsToMove,
          folder.id
        );
        lastSelectedShapeIdRef.current = null;

        logEvent({
          eventName: 'Shapes Moved to Group',
          eventType: EventType.Modified,
          resourceName: folder.name,
          resourceType: EventResource.Shape,
          metadata: {
            affectedResources: [...shapeIdsToMove, ...sheetShapeIdsToMove],
            details: `${shapesToMove.length} shape${
              shapesToMove.length === 1 ? '' : 's'
            } moved to group ${folder.name}`,
          },
        });
      } else {
        await removeShapesFromFolder(shapeIdsToMove, sheetShapeIdsToMove);

        logEvent({
          eventName: 'Shapes Removed from Group',
          eventType: EventType.Modified,
          resourceName: 'Shapes',
          resourceType: EventResource.Shape,
          metadata: {
            affectedResources: [...shapeIdsToMove, ...sheetShapeIdsToMove],
            details: `${shapesToMove.length} shape${
              shapesToMove.length === 1 ? '' : 's'
            } removed from group`,
          },
        });
      }
    },
    [moveShapesToFolder, removeShapesFromFolder, logEvent]
  );

  const handleDragOverOutside = (e: React.DragEvent) => {
    e.preventDefault();
    setIsHoveredOutside(true);
  };

  const handleDragLeaveOutside = () => {
    setIsHoveredOutside(false);
  };

  const handleDropOutside = (e: React.DragEvent) => {
    e.preventDefault();
    const draggedShapes = JSON.parse(e.dataTransfer.getData('text/plain')) as
      | (ShapeDeepFragment | SheetShapeDeepFragment)[];

    // Move all selected shapes out of any folder
    if (draggedShapes && draggedShapes.length > 0) {
      handleShapeMove(draggedShapes, null);
    }
    setIsHoveredOutside(false);
  };

  const handleRename = useCallback(
    // eslint-disable-next-line complexity
    async (node: ShapeNodeType, newName: string) => {
      // Check if the node is part of a selection and if all selected items are Shape or SheetShape
      const isShapePartOfSelection = selectedShapesRef.current.find(
        (ss) => ss.id === node.id
      );
      const isValidSelection = selectedShapesRef.current.every(
        (shape) =>
          shape.__typename === 'Shape' || shape.__typename === 'SheetShape'
      );

      if (
        (node.__typename === 'Shape' || node.__typename === 'SheetShape') &&
        isShapePartOfSelection &&
        isValidSelection
      ) {
        // Split shapes by type
        const shapes3D = selectedShapesRef.current.filter(
          (shape): shape is ShapeDeepFragment => shape.__typename === 'Shape'
        );
        const sheetShapes = selectedShapesRef.current.filter(
          (shape): shape is SheetShapeDeepFragment =>
            shape.__typename === 'SheetShape'
        );

        try {
          // Execute all mutations in parallel
          await Promise.all([
            ...shapes3D.map((shape) =>
              updateShapeMutation({
                input: {
                  id: shape.id,
                  patch: { name: newName },
                },
              })
            ),
            ...sheetShapes.map((shape) =>
              updateSheetShapeMutation({
                input: {
                  id: shape.id,
                  patch: { name: newName },
                },
              })
            ),
          ]);
        } catch (error) {
          console.error(error);
          toast({
            title: t('project:shapes.error.rename.title'),
            status: 'error',
          });
        } finally {
          logEvent({
            eventName: 'Shapes Renamed',
            eventType: EventType.Modified,
            resourceName: newName,
            resourceType: EventResource.Shape,
            metadata: {
              affectedResources: [
                ...selectedShapesRef.current.map((shape) => shape.id),
              ],
              oldName:
                selectedShapesRef.current.length > 1
                  ? undefined
                  : selectedShapesRef.current[0].name,
              newName,
              details: `${selectedShapesRef.current.length} shapes renamed to ${newName}`,
            },
          });
        }
      } else {
        // Handle single item rename as before
        if (node.__typename === 'Shape') {
          const { error } = await updateShapeMutation({
            input: {
              id: node.id,
              patch: { name: newName },
            },
          });
          if (error) {
            console.error(error);
            toast({
              title: t('project:shapes.error.rename.title'),
              status: 'error',
            });
          } else {
            logEvent({
              eventName: 'Shape Renamed',
              eventType: EventType.Modified,
              resourceName: node.name,
              resourceType: EventResource.Shape,
              metadata: {
                affectedResources: [node.id],
                oldName: node.name,
                newName,
              },
            });
          }
        } else if (node.__typename === 'SheetShape') {
          const { error } = await updateSheetShapeMutation({
            input: {
              id: node.id,
              patch: { name: newName },
            },
          });
          if (error) {
            console.error(error);
            toast({
              title: t('project:shapes.error.rename.title'),
              status: 'error',
            });
          } else {
            logEvent({
              eventName: 'Shape Renamed',
              eventType: EventType.Modified,
              resourceName: node.name,
              resourceType: EventResource.SheetShape,
              metadata: {
                oldName: node.name,
                newName: newName,
                affectedResources: [node.id],
              },
            });
          }
        } else if (node.__typename === 'AutoShape') {
          await renameAutoShape(node.id, newName);
          logEvent({
            eventName: 'Auto Shape Renamed',
            eventType: EventType.Modified,
            resourceName: node.name,
            resourceType: EventResource.ShapeFolder,
            metadata: {
              affectedResources: [node.id],
              oldName: node.name,
              newName,
            },
          });
        } else {
          await renameFolder(node.id, newName);
          logEvent({
            eventName: 'Group Renamed',
            eventType: EventType.Modified,
            resourceName: node.name,
            resourceType: EventResource.ShapeFolder,
            metadata: {
              affectedResources: [node.id],
              oldName: node.name,
              newName,
            },
          });
        }
      }
    },
    [
      logEvent,
      renameAutoShape,
      renameFolder,
      t,
      toast,
      updateShapeMutation,
      updateSheetShapeMutation,
    ]
  );

  const handleShow = useCallback(
    (node: ShapeNodeType) => {
      if (node.__typename === 'Shape' || node.__typename === 'SheetShape') {
        const isShapePartOfSelection = selectedShapesRef.current.find(
          (ss) => ss.id === node.id
        );
        let dbidsPerModelToShow = { [node.urn]: [node.dbId] };
        if (isShapePartOfSelection) {
          const dbIdsPerModel: DbIdsPerModel = {};
          for (const shape of selectedShapesRef.current) {
            if (!dbIdsPerModel[shape.urn]) {
              dbIdsPerModel[shape.urn] = [];
            }
            dbIdsPerModel[shape.urn].push(shape.dbId);
          }
          dbidsPerModelToShow = dbIdsPerModel;
        }
        show(dbidsPerModelToShow);
      } else {
        const shapesInFolder = shapesRef.current.filter(
          (s) => s.folder === node.id || s.autoShapeId === node.id
        );
        const dbIdsPerModel: DbIdsPerModel = {};
        for (const shape of shapesInFolder) {
          if (!dbIdsPerModel[shape.urn]) {
            dbIdsPerModel[shape.urn] = [];
          }
          dbIdsPerModel[shape.urn].push(shape.dbId);
        }
        show(dbIdsPerModel);
      }
    },
    [show]
  );

  const handleHide = useCallback(
    (node: ShapeNodeType) => {
      if (node.__typename === 'Shape' || node.__typename === 'SheetShape') {
        const isShapePartOfSelection = selectedShapesRef.current.find(
          (ss) => ss.id === node.id
        );
        let dbidsPerModelToHide = { [node.urn]: [node.dbId] };
        if (isShapePartOfSelection) {
          const dbIdsPerModel: DbIdsPerModel = {};
          for (const shape of selectedShapesRef.current) {
            if (!dbIdsPerModel[shape.urn]) {
              dbIdsPerModel[shape.urn] = [];
            }
            dbIdsPerModel[shape.urn].push(shape.dbId);
          }
          dbidsPerModelToHide = dbIdsPerModel;
        }
        hide(dbidsPerModelToHide);
      } else {
        const shapesInFolder = shapesRef.current.filter(
          (s) => s.folder === node.id || s.autoShapeId === node.id
        );
        const dbIdsPerModel: DbIdsPerModel = {};
        for (const shape of shapesInFolder) {
          if (!dbIdsPerModel[shape.urn]) {
            dbIdsPerModel[shape.urn] = [];
          }
          dbIdsPerModel[shape.urn].push(shape.dbId);
        }
        hide(dbIdsPerModel);
      }
    },
    [hide]
  );

  const [nodesToDelete, setNodesToDelete] = useState<ShapeNodeType[]>([]);

  // Helper function to group nodes by type
  const groupNodesByType = useCallback((nodesToDelete: ShapeNodeType[]) => {
    const shapes: ShapeNodeType[] = [];
    const sheetShapes: ShapeNodeType[] = [];
    const autoShapes: ShapeNodeType[] = [];
    const folders: ShapeNodeType[] = [];

    for (const node of nodesToDelete) {
      if (node.__typename === 'Shape') {
        shapes.push(node);
      } else if (node.__typename === 'SheetShape') {
        sheetShapes.push(node);
      } else if (node.__typename === 'AutoShape') {
        autoShapes.push(node);
      } else {
        folders.push(node);
      }
    }

    return { shapes, sheetShapes, autoShapes, folders };
  }, []);

  // Helper function to delete shapes in a folder
  const deleteShapesInFolder = useCallback(
    async (folderId: string) => {
      const shapesInFolder = shapesRef.current.filter(
        (s) => s.folder === folderId
      );
      const BIMShapes = shapesInFolder.filter((s) => s.__typename === 'Shape');
      const sheetShapesInFolder = shapesInFolder.filter(
        (s) => s.__typename === 'SheetShape'
      );

      // Cleanup Protan roof flags for drain line shapes inside the folder before deletion
      await cleanupRoofFlagsForDeletedDrainLines(
        sheetShapesInFolder as (ShapeDeepFragment | SheetShapeDeepFragment)[],
        sparkelPropsData,
        projectId,
        isProtan
      );

      // Delete all shapes in the folder
      for (const s of BIMShapes) {
        await deleteShapeMutation({ input: { id: s.id } });
      }

      for (const s of sheetShapesInFolder) {
        await deleteSheetShapeMutation({ input: { id: s.id } });
      }
    },
    [
      deleteShapeMutation,
      deleteSheetShapeMutation,
      cleanupRoofFlagsForDeletedDrainLines,
      sparkelPropsData,
      projectId,
      isProtan,
    ]
  );

  // Helper function to log deletion events
  const logDeletionEvents = useCallback(
    (
      shapes: ShapeNodeType[],
      sheetShapes: ShapeNodeType[],
      autoShapes: ShapeNodeType[],
      folders: ShapeNodeType[]
    ) => {
      if (shapes.length > 0) {
        logEvent({
          eventName: 'Shapes Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Shapes',
          resourceType: EventResource.Shape,
          metadata: {
            details: `${shapes.length} shapes deleted`,
            affectedResources: shapes.map((node) => node.id),
          },
        });
      }

      if (sheetShapes.length > 0) {
        logEvent({
          eventName: 'Sheet Shapes Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Sheet Shapes',
          resourceType: EventResource.SheetShape,
          metadata: {
            details: `${sheetShapes.length} sheet shapes deleted`,
            affectedResources: sheetShapes.map((node) => node.id),
          },
        });
      }

      if (autoShapes.length > 0) {
        logEvent({
          eventName: 'Auto Shapes Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Auto Shapes',
          resourceType: EventResource.Shape,
          metadata: {
            details: `${autoShapes.length} auto shapes deleted`,
            affectedResources: autoShapes.map((node) => node.id),
          },
        });
      }

      if (folders.length > 0) {
        logEvent({
          eventName: 'Shape Folders Deleted',
          eventType: EventType.Deleted,
          resourceName: 'Shape Folders',
          resourceType: EventResource.ShapeFolder,
          metadata: {
            details: `${folders.length} folders deleted`,
            affectedResources: folders.map((node) => node.id),
          },
        });
      }
    },
    [logEvent]
  );

  const handleDelete = useCallback(
    async (nodesToDelete: ShapeNodeType[]) => {
      setSelectedDbIds({});

      const { shapes, sheetShapes, autoShapes, folders } =
        groupNodesByType(nodesToDelete);

      // If Protan: analyze drain line sheet shapes that will be deleted to clean up roof flags
      await cleanupRoofFlagsForDeletedDrainLines(
        sheetShapes,
        sparkelPropsData,
        projectId,
        isProtan
      );

      // Delete individual shapes
      for (const node of shapes) {
        await deleteShapeMutation({ input: { id: node.id } });
      }

      for (const node of sheetShapes) {
        await deleteSheetShapeMutation({ input: { id: node.id } });
      }

      for (const node of autoShapes) {
        await deleteAutoShape(node.id);
      }

      // Handle folders - delete all shapes in the folder first, then the folder
      for (const folder of folders) {
        await deleteShapesInFolder(folder.id);
        await deleteFolder(folder.id);
      }

      // Log events for different types
      logDeletionEvents(shapes, sheetShapes, autoShapes, folders);
    },
    [
      deleteAutoShape,
      deleteFolder,
      deleteShapeMutation,
      deleteSheetShapeMutation,
      setSelectedDbIds,
      groupNodesByType,
      deleteShapesInFolder,
      logDeletionEvents,
    ]
  );

  const onDeleteConfirm = useCallback(async () => {
    await handleDelete(nodesToDelete);
    setNodesToDelete([]);
    onDeleteSelectedShapesModalClose();
    logEvent({
      projectId,
      eventName: 'Shapes Deleted',
      eventType: EventType.Deleted,
      resourceName: 'Shapes',
      resourceType: EventResource.Shape,
      resolved: false,
      metadata: {
        details: `${nodesToDelete.length} shapes deleted`,
        affectedResources: nodesToDelete.map((node) => node.id),
      },
    });
  }, [
    handleDelete,
    logEvent,
    nodesToDelete,
    onDeleteSelectedShapesModalClose,
    projectId,
  ]);

  // Context menu
  const {
    registerItem: registerSheetItem,
    unregisterItem: unregisterSheetItem,
  } = useSheetContextMenuContext();
  const {
    registerItem: registerSheetViewerV2Item,
    unregisterItem: unregisterSheetViewerV2Item,
  } = useSheetViewerV2ContextMenuContext();
  const { registerItem: registerBIMItem, unregisterItem: unregisterBIMItem } =
    useForgeContextMenuContext();

  const registerItem =
    viewerMode === ViewerMode.Models
      ? registerBIMItem
      : viewerMode === ViewerMode.Sheets
      ? registerSheetItem
      : registerSheetViewerV2Item;

  const unregisterItem =
    viewerMode === ViewerMode.Models
      ? unregisterBIMItem
      : viewerMode === ViewerMode.Sheets
      ? unregisterSheetItem
      : unregisterSheetViewerV2Item;

  const handleEdit = useCallback(() => {
    if (selectedShapes.length === 0) {
      return;
    }
    const shape = selectedShapes[0];
    if (viewerMode === ViewerMode.Models) {
      edit3dShape(shape.id);
    } else {
      editSheetShape(shape.id);
    }
  }, [selectedShapes, viewerMode, edit3dShape, editSheetShape]);

  useEffect(() => {
    if (readOnly || !selectedShapes.length) {
      return;
    }

    const shape = selectedShapes[0];

    registerItem({
      id: `rename-shape-${shape.id}`,
      label: t('project:shapes.rename-shape'),
      group: 'shapes',
      icon: <SparkelIcon size="sm" icon={faPen} fixedWidth />,
      hidden: (params) => {
        // Show when there is any selection; hide only if nothing is selected
        return !params.props?.hasSelectedElements;
      },
      onClick: ({
        triggerEvent,
      }: {
        triggerEvent: React.MouseEvent | React.TouchEvent | React.KeyboardEvent;
      }) => {
        let x = 0;
        let y = 0;
        if (
          triggerEvent &&
          'clientX' in triggerEvent &&
          'clientY' in triggerEvent
        ) {
          const evt = triggerEvent as React.MouseEvent;
          x = evt.clientX;
          y = evt.clientY;
        }
        openRenamePopover(shape, { x, y });
      },
    });

    registerItem({
      id: `edit-shape-${shape.id}`,
      label: t('project:shapes.edit-shape'),
      group: 'shapes',
      icon: <SparkelIcon size="sm" icon={faEdit} fixedWidth />,
      hidden: (params) => {
        if (typeof params.props?.target === 'undefined') {
          return true;
        }

        return !(
          params.props.target.dbId === shape.dbId &&
          params.props.target.modelUrn === shape.urn
        );
      },
      onClick: handleEdit,
    });

    registerItem({
      id: 'delete-selected-shapes',
      label: t('project:shapes.delete'),
      group: 'shapes',
      icon: (
        <SparkelIcon size="sm" icon={faTrash} color={'red.400'} fixedWidth />
      ),
      hidden: (params) => {
        if (!params.props?.hasSelectedElements) {
          return true;
        }

        return false;
      },
      onClick: () => {
        setNodesToDelete(selectedShapes);
        onDeleteSelectedShapesModalOpen();
      },
    });
    return () => {
      unregisterItem(`rename-shape-${shape.id}`);
      unregisterItem(`edit-shape-${shape.id}`);
      unregisterItem('delete-selected-shapes');
    };
  }, [
    edit3dShape,
    editSheetShape,
    handleEdit,
    openRenamePopover,
    onDeleteSelectedShapesModalOpen,
    readOnly,
    registerItem,
    selectedShapes,
    t,
    unregisterItem,
    viewerMode,
  ]);

  const onDeleteHandler = useCallback((node: ShapeNodeType) => {
    const isNodeSeleceted = selectedShapesRef.current.find(
      (ss) => ss.id === node.id
    );
    const nodes = isNodeSeleceted ? selectedShapesRef.current : [node];
    setNodesToDelete(nodes);
    onDeleteSelectedShapesModalOpen();
  }, []);

  const outsideOutline = isHoveredOutside
    ? `2px solid ${ChakraColor.blue300}`
    : 'none';

  const areAllSelectedShapes3d = selectedShapes.every(
    (shape) => shape.__typename === 'Shape'
  );
  const areAllSelectedShapes2d = selectedShapes.every(
    (shape) => shape.__typename === 'SheetShape'
  );
  const isSomeSelectedShapeAutoShape = selectedShapes.some(
    (shape) => shape.autoShapeId !== null
  );

  const largestExpandedFolder = virtualItems
    .filter((vi) => {
      const found = sortedFolders.find((sf) => sf.id === vi.key);
      if (found) {
        return expandedFolders[found.id];
      }
      return false;
    })
    .at(-1); // The assumption is that these are always in order

  // Find and remove all persisted folders that are not in virtual Items and appear after the largest expanded folder.
  for (const [k, v] of persistedVirtualFoldersRef.current) {
    const found = virtualItems.find((vi) => vi.key === k);
    if (
      !found &&
      largestExpandedFolder &&
      v.start > largestExpandedFolder.start
    ) {
      persistedVirtualFoldersRef.current.delete(k);
    }
  }

  virtualItems.forEach((item) => {
    const found = sortedFolders.find((sf) => sf.id === item.key);
    if (found) {
      const isExpanded = expandedFolders[found.id];
      if (isExpanded) {
        persistedVirtualFoldersRef.current.set(item.key, item);
      }
      if (!isExpanded && persistedVirtualFoldersRef.current.has(item.key)) {
        persistedVirtualFoldersRef.current.delete(item.key);
      }
    }
  });

  const persistedVirtualItems = virtualItems;

  for (const [k, v] of persistedVirtualFoldersRef.current) {
    const found = persistedVirtualItems.find((vf) => vf.key === k);
    if (!found) {
      persistedVirtualItems.push(v);
    }
  }

  const virtualFolders = persistedVirtualItems.filter((virtualItem) => {
    const found = sortedFolders.find((folder) => folder.id === virtualItem.key);
    return !!found;
  });

  const virtualShapesWithoutFolders = virtualItems.filter((virtualItem) => {
    const found = shapesWithNoFolder.find(
      (shape) => shape.id === virtualItem.key
    );
    return !!found;
  });

  const firstVirtualItemWithoutFolder = virtualItems.find(
    (item) => item.key === shapesWithNoFolder[0]?.id
  );

  if (firstVirtualItemWithoutFolder) {
    firstShapeWithoutFolder.current = firstVirtualItemWithoutFolder;
  }

  const vShapeWithoutFoldersContainerTop = firstShapeWithoutFolder.current
    ?.start
    ? firstShapeWithoutFolder.current.start
    : 0;
  const shapeWithoutFoldersContainerHeight =
    SHAPE_ITEM_SIZE * shapesWithNoFolder.length;

  const isMovingOrCopying =
    isMovingShapes3D ||
    isCopyingShapes3D ||
    isMovingShapes2D ||
    isCopyingShapes2D;

  const modelUrn = useMemo(() => {
    return activeModels?.[0]?.forgeUrn ?? '';
  }, [activeModels]);

  const modelId = useMemo(() => {
    return activeModels?.[0]?.id ?? '';
  }, [activeModels]);

  const sheetId = useMemo(() => {
    return activeSheetId ?? '';
  }, [activeSheetId]);

  const pageNumber = useMemo(() => {
    return activeSheetPageNumber ?? 0;
  }, [activeSheetPageNumber]);

  const getShapeDiff = useCallback(
    // eslint-disable-next-line complexity
    (shape: ShapeDeepFragment | SheetShapeDeepFragment): ShapeItemDiff => {
      const isSheetShape = shape.__typename === 'SheetShape';
      const shapesKey = isSheetShape ? 'loadedSheetShapes' : 'loadedShapes';
      const nameKey = `propertyContext.${shapesKey}.${shape.urn}.${shape.id}.name`;
      const shapeKey = `propertyContext.${shapesKey}.${shape.urn}.${shape.id}`;
      const collectionKey = `propertyContext.${shapesKey}.${shape.urn}`;

      // Check for shape creation/deletion at collection level (first shape creation)
      if (diff?.[collectionKey]) {
        const collectionDiff = diff[collectionKey];
        if (
          collectionDiff.new &&
          typeof collectionDiff.new === 'object' &&
          collectionDiff.new[shape.id]
        ) {
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: shape.name,
          };
        }
      }

      // Check for shape creation/deletion at individual level
      if (diff?.[shapeKey]) {
        const shapeDiff = diff[shapeKey];
        if (shapeDiff.new && !shapeDiff.old) {
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: shape.name,
          };
        }
        if (shapeDiff.old && !shapeDiff.new) {
          return {
            eventType: EventType.Deleted,
            oldName: shape.name,
            newName: undefined,
          };
        }
      }

      // Check for name changes
      if (diff?.[nameKey]) {
        const nameDiff = diff[nameKey];
        return {
          eventType: EventType.Modified,
          oldName: nameDiff.old,
          newName: nameDiff.new,
        };
      }

      // Check for other shape property changes (non-name)
      if (diff) {
        const hasOtherChanges = Object.keys(diff).some(
          (key) => key.startsWith(shapeKey) && !key.endsWith('.name')
        );
        if (hasOtherChanges) {
          return {
            eventType: EventType.Modified,
            oldName: shape.name,
            newName: shape.name,
          };
        }
      }

      // Check for attribute changes
      const attributeDiff = getAttributeDiff(
        referenceState,
        diff,
        shape.urn,
        shape.dbId
      );
      if (attributeDiff) {
        const eventType = getEventType(attributeDiff);
        if (eventType) {
          return {
            eventType,
            oldName: shape.name,
            newName: shape.name,
          };
        }
      }

      // No changes - return current name
      return {
        oldName: shape.name,
        newName: shape.name,
      };
    },
    [diff, referenceState]
  );

  const getFolderDiff = useCallback(
    // eslint-disable-next-line complexity
    (
      folder: ShapeFolderDeepFragment | AutoShapeDeepFragment
    ): ShapeItemDiff => {
      // For shape folders, we need to use the project URN since folders don't have their own URN
      const projectUrn = getShapeUrn(projectId ?? '');
      const folderKey = `propertyContext.loadedShapeFolders.${projectUrn}.${folder.id}`;
      const nameKey = `propertyContext.loadedShapeFolders.${projectUrn}.${folder.id}.name`;
      const collectionKey = `propertyContext.loadedShapeFolders.${projectUrn}`;

      // 1. Check for folder creation/deletion at collection level (first folder creation)
      if (diff?.[collectionKey]) {
        const collectionDiff = diff[collectionKey];
        if (
          collectionDiff.new &&
          typeof collectionDiff.new === 'object' &&
          collectionDiff.new[folder.id]
        ) {
          // Folder created as part of collection replacement
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: folder.name,
          };
        }
      }

      // 2. Check for folder creation/deletion at individual level
      if (diff?.[folderKey]) {
        const folderDiff = diff[folderKey];
        if (folderDiff.new && !folderDiff.old) {
          // Folder created
          return {
            eventType: EventType.Created,
            oldName: undefined,
            newName: folder.name,
          };
        }
        if (folderDiff.old && !folderDiff.new) {
          // Folder deleted
          return {
            eventType: EventType.Deleted,
            oldName: folder.name,
            newName: undefined,
          };
        }
      }

      // 3. Check for name changes
      if (diff?.[nameKey]) {
        const nameDiff = diff[nameKey];
        return {
          eventType: EventType.Modified,
          oldName: nameDiff.old,
          newName: nameDiff.new,
        };
      }

      // 4. Check for other folder property changes (non-name)
      if (diff) {
        const hasOtherChanges = Object.keys(diff).some(
          (key) => key.startsWith(folderKey) && !key.endsWith('.name')
        );
        if (hasOtherChanges) {
          return {
            eventType: EventType.Modified,
            oldName: folder.name,
            newName: folder.name,
          };
        }
      }

      // 5. Check if any shapes within the folder have been modified
      if (diff) {
        const shapesInFolder = shapes.filter(
          (shape) =>
            shape.folder === folder.id || shape.autoShapeId === folder.id
        );

        const hasShapeChanges = shapesInFolder.some((shape) => {
          const shapeDiff = getShapeDiff(shape);
          return shapeDiff.eventType !== undefined;
        });

        if (hasShapeChanges) {
          return {
            eventType: EventType.Modified,
            oldName: folder.name,
            newName: folder.name,
          };
        }
      }

      // 6. No changes - return current name
      return {
        oldName: folder.name,
        newName: folder.name,
      };
    },
    [diff, projectId, shapes, getShapeDiff]
  );

  return (
    <>
      <Panel
        resizeable
        panelContentRef={panelRef}
        setPanelHeight={setShapesPanelHeight}
      >
        <PanelHeader>
          <PanelTitle>{t('shapes')}</PanelTitle>
          <PanelActionButtons>
            {selectedShapes.length > 0 &&
            !readOnly &&
            !isMovingOrCopying &&
            (areAllSelectedShapes3d || areAllSelectedShapes2d) &&
            !isSomeSelectedShapeAutoShape ? (
              <>
                <Tooltip
                  label={t('project:shapes.move-shape')}
                  placement="top-start"
                >
                  <IconButton
                    icon={<SparkelIcon icon={faArrows} />}
                    colorScheme="gray"
                    aria-label="Move shape"
                    size="xs"
                    borderRadius="xl"
                    onClick={() =>
                      viewerMode === ViewerMode.Models
                        ? setMovingShapes3D(true)
                        : setMovingShapes2D(true)
                    }
                  />
                </Tooltip>
                <Tooltip
                  label={t('project:shapes.copy-shape')}
                  placement="top-start"
                >
                  <IconButton
                    icon={<SparkelIcon icon={faCopy} />}
                    colorScheme="gray"
                    aria-label="Copy shape"
                    size="xs"
                    borderRadius="xl"
                    onClick={() =>
                      viewerMode === ViewerMode.Models
                        ? setCopyingShapes3D(true)
                        : setCopyingShapes2D(true)
                    }
                  />
                </Tooltip>
              </>
            ) : null}
            {isMovingOrCopying && (
              <Tooltip label={t('project:shapes.cancel-drawing')}>
                <IconButton
                  aria-label="cancel moving/copying"
                  icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
                  colorScheme="gray"
                  size={'xs'}
                  onClick={() => {
                    setCopyingShapes3D(false);
                    setMovingShapes3D(false);
                    setCopyingShapes2D(false);
                    setMovingShapes2D(false);
                  }}
                />
              </Tooltip>
            )}
            {isDrawing3d || isDrawingSheets ? (
              <>
                <Tooltip label={t('project:shapes.cancel-drawing')}>
                  <IconButton
                    aria-label="cancel shape"
                    icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
                    colorScheme="gray"
                    size={'xs'}
                    onClick={() => {
                      if (viewerMode === ViewerMode.Models) {
                        console.log('deactivate3dDrawing');
                        deactivate3dDrawing();
                      } else {
                        console.log('deactivateSheetDrawing');
                        deactivateSheetDrawing();
                      }
                    }}
                  />
                </Tooltip>
                <Tooltip
                  label={
                    hasDrawingResult
                      ? t('project:shapes.save-tooltip')
                      : t('project:shapes.save-disabled-tooltip')
                  }
                >
                  <IconButton
                    aria-label="submit shape"
                    icon={<SparkelIcon icon={faCheck} color={'inherit'} />}
                    isDisabled={!hasDrawingResult}
                    size={'xs'}
                    onClick={() => {
                      if (viewerMode === ViewerMode.Models) {
                        submit3dDrawing();
                      } else {
                        submitSheetDrawing();
                      }
                    }}
                  />
                </Tooltip>
              </>
            ) : (
              !isMovingOrCopying &&
              !readOnly &&
              (enableAutoShapes ? (
                <ButtonGroup isAttached colorScheme="brand" variant={'outline'}>
                  <Tooltip label={t('project:shapes.create')}>
                    <Button
                      aria-label="New table modal"
                      size="xs"
                      borderRadius="md"
                      onClick={() => {
                        if (viewerMode === 'models') {
                          activate3dDrawing();
                        } else {
                          activateSheetDrawing();
                        }
                      }}
                    >
                      {t('project:shapes.button.add')}
                    </Button>
                  </Tooltip>
                  <Menu isLazy closeOnBlur={false}>
                    {({ onClose }) => (
                      <>
                        <MenuButton
                          as={IconButton}
                          size="xs"
                          borderRadius="md"
                          aria-label="add-shape-options"
                          icon={
                            <SparkelIcon
                              icon={faChevronDown}
                              color={'inherit'}
                            />
                          }
                        />
                        <Portal>
                          <AutoshapePopover
                            viewerMode={viewerMode}
                            createAutoShape={createAutoShape}
                            onClose={onClose}
                            findNextAvailableAutoShapeName={
                              findNextAvailableAutoShapeName
                            }
                            modelId={modelId}
                            modelUrn={modelUrn}
                            globalOffset={globalOffset ?? { x: 0, y: 0, z: 0 }}
                            sheetId={sheetId}
                            pageNumber={pageNumber}
                            templateRect={{
                              x: 100,
                              y: 100,
                              width: 50,
                              height: 50,
                            }}
                            sensitivity={0.5}
                          />
                        </Portal>
                      </>
                    )}
                  </Menu>
                </ButtonGroup>
              ) : (
                <Tooltip label={t('project:shapes.create')}>
                  <IconButton
                    aria-label="new shape"
                    icon={<SparkelIcon icon={faPlus} color={'inherit'} />}
                    size={'xs'}
                    isDisabled={isProtan && !orderId}
                    onClick={() => {
                      if (viewerMode === ViewerMode.Models) {
                        console.log('activate3dDrawing');
                        activate3dDrawing();
                      } else {
                        console.log('activateSheetDrawing');
                        activateSheetDrawing();
                      }
                    }}
                  />
                </Tooltip>
              ))
            )}
          </PanelActionButtons>
        </PanelHeader>
        <PanelContent
          ref={panelRef}
          minHeight={6}
          maxHeight={'70vh'}
          paddingLeft={0}
          height={shapesPanelHeight}
          userSelect={'none'}
        >
          <Box
            height={`${shapeVirtualizer.getTotalSize()}px`}
            position="relative"
          >
            {sortedFolders.length > 0 ? (
              <>
                {virtualFolders.map((virtualItem) => {
                  return (
                    <ShapesFolder
                      key={virtualItem.key}
                      {...{
                        sortedFolders,
                        virtualItem,
                        shapesIndexedByFolder,
                        virtualItems,
                        hiddenBIMElements,
                        hiddenSheetElements,
                        expandedFolders,
                        selectedFolder,
                        selectedShapes,
                        toggleFolderExpansion,
                        handleRename,
                        handleHide,
                        handleShow,
                        handleShapeMove,
                        handleNodeSelect,
                        onDeleteHandler,
                        changeActiveSheet,
                        edit3dShape,
                        editSheetShape,
                        setPageNumber,
                        setViewerMode,
                        ungroupShapesFromFolder,
                        deleteFolder,
                        groupShapesIntoFolder,
                        selectedShapesRef,
                        selectedDbIds,
                        isDiffing: isHistoryDrawerOpen,
                        getShapeDiff,
                        getFolderDiff,
                      }}
                    />
                  );
                })}
                <Box
                  onDragOver={handleDragOverOutside}
                  onDrop={handleDropOutside}
                  onDragLeave={handleDragLeaveOutside}
                  outline={outsideOutline}
                  outlineOffset={-2}
                  width={'100%'}
                  borderRadius="md"
                  height={`${shapeWithoutFoldersContainerHeight}px`}
                  style={{ top: `${vShapeWithoutFoldersContainerTop}px` }}
                  position="relative"
                >
                  {virtualShapesWithoutFolders.map((virtualItem) => {
                    const shape = shapesWithNoFolder.find(
                      (shape) => shape.id === virtualItem.key
                    );
                    if (!shape) return null;
                    return (
                      <ShapeItem
                        isDiffing={isHistoryDrawerOpen}
                        diff={getShapeDiff(shape)}
                        key={virtualItem.key}
                        node={shape}
                        isSelected={isShapeSelected(shape, selectedDbIds)}
                        onShapeSelect={handleNodeSelect}
                        onMoveNode={handleShapeMove}
                        onRename={handleRename}
                        onShow={handleShow}
                        onHide={handleHide}
                        onDelete={onDeleteHandler}
                        isHidden={isShapeHidden(
                          shape,
                          hiddenBIMElements,
                          hiddenSheetElements
                        )}
                        changeActiveSheet={changeActiveSheet}
                        editBIMShape={edit3dShape}
                        setPageNumber={setPageNumber}
                        editSheetShape={editSheetShape}
                        setViewerMode={setViewerMode}
                        removeShapesFromFolder={ungroupShapesFromFolder}
                        deleteFolder={deleteFolder}
                        groupShapesIntoFolder={groupShapesIntoFolder}
                        selectedShapesRef={selectedShapesRef}
                        top={
                          virtualItem.start - vShapeWithoutFoldersContainerTop
                        }
                        height={virtualItem.size}
                      />
                    );
                  })}
                </Box>
              </>
            ) : shapes.length > 0 ? (
              virtualItems.map((virtualItem) => {
                const shape = sortedShapes.find(
                  (sshape) => sshape.id === virtualItem.key
                );
                if (!shape) return null;
                return (
                  <ShapeItem
                    isDiffing={isHistoryDrawerOpen}
                    diff={getShapeDiff(shape)}
                    key={virtualItem.key}
                    node={shape}
                    isSelected={selectedShapes.some((s) => s.id === shape.id)}
                    onShapeSelect={handleNodeSelect}
                    onMoveNode={handleShapeMove}
                    onRename={handleRename}
                    onShow={handleShow}
                    onHide={handleHide}
                    onDelete={onDeleteHandler}
                    isHidden={isShapeHidden(
                      shape,
                      hiddenBIMElements,
                      hiddenSheetElements
                    )}
                    changeActiveSheet={changeActiveSheet}
                    editBIMShape={edit3dShape}
                    setPageNumber={setPageNumber}
                    editSheetShape={editSheetShape}
                    setViewerMode={setViewerMode}
                    removeShapesFromFolder={ungroupShapesFromFolder}
                    deleteFolder={deleteFolder}
                    groupShapesIntoFolder={groupShapesIntoFolder}
                    selectedShapesRef={selectedShapesRef}
                    top={virtualItem.start}
                    height={virtualItem.size}
                  />
                );
              })
            ) : (
              <PlaceholderText margin={2}>No shapes created</PlaceholderText>
            )}
          </Box>
        </PanelContent>
      </Panel>
      <ConfirmDeletionModal
        onConfirm={onDeleteConfirm}
        isOpen={showDeleteSelectedShapesModal}
        onClose={onDeleteSelectedShapesModalClose}
        itemsToDelete={nodesToDelete.map((e) => e.name)}
      />
      {/* Rename Popover anchored at cursor position */}
      <Popover
        isOpen={isRenamePopoverOpen}
        onClose={closeRenamePopover}
        autoFocus={false}
        placement="bottom-start"
        isLazy
      >
        <PopoverAnchor>
          <Box
            position="fixed"
            pointerEvents="none"
            style={{
              left: renameAnchorPos?.x ?? -9999,
              top: renameAnchorPos?.y ?? -9999,
            }}
          />
        </PopoverAnchor>
        <Portal>
          <PopoverContent width="xs" p={1} zIndex={9999} borderRadius="xl">
            <PopoverBody p={1}>
              <HStack>
                <Input
                  ref={renameInputRef}
                  size="sm"
                  variant={'unstyled'}
                  borderRadius={0}
                  value={renameInput}
                  onChange={(e) => setRenameInput(e.target.value)}
                  onFocus={(e) => e.currentTarget.select()}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      closeRenamePopover();
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (renameTarget) {
                        const trimmed = renameInput.trim();
                        if (!trimmed || trimmed === renameTarget.name) {
                          closeRenamePopover();
                        } else {
                          void handleRename(renameTarget, trimmed).then(() => {
                            closeRenamePopover();
                          });
                        }
                      }
                    }
                  }}
                />
                <IconButton
                  aria-label={t('common:cancel')}
                  size="xs"
                  colorScheme="gray"
                  icon={<SparkelIcon icon={faTimes} color={'inherit'} />}
                  onClick={closeRenamePopover}
                />
                <IconButton
                  aria-label={t('common:save')}
                  size="xs"
                  icon={<SparkelIcon icon={faCheck} color={'inherit'} />}
                  onClick={() => {
                    if (renameTarget) {
                      const trimmed = renameInput.trim();
                      if (!trimmed || trimmed === renameTarget.name) {
                        closeRenamePopover();
                      } else {
                        void handleRename(renameTarget, trimmed).then(() => {
                          closeRenamePopover();
                        });
                      }
                    }
                  }}
                />
              </HStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </>
  );
}

export const isShapeSelected = (
  shape: ShapeDeepFragment | SheetShapeDeepFragment,
  selectedDbIds: Record<string, number[]>
) => {
  const shapeUrn = shape.urn;
  return (
    shapeUrn in selectedDbIds && selectedDbIds[shapeUrn].includes(shape.dbId)
  );
};

export const isShapeHidden = (
  node: ShapeNodeType,
  hiddenBimElements: DbIdsPerModel,
  hiddenSheetElements: DbIdsPerModel,
  allChildrenHidden = false
) => {
  if (allChildrenHidden) return true;
  if (node.__typename === 'Shape' && hiddenBimElements[node.urn]) {
    return hiddenBimElements[node.urn].includes(node.dbId);
  }
  if (node.__typename === 'SheetShape' && hiddenSheetElements[node.urn]) {
    return hiddenSheetElements[node.urn].includes(node.dbId);
  }
  return false;
};

export default memo(ShapesPanel);

const compareShapeNameWithNumber = (a: string, b: string) => {
  // Check if the strings end with a number
  const regex = /(\d+)$/;
  const aMatch = a.trim().match(regex);
  const bMatch = b.trim().match(regex);

  // If both strings end with numbers, compare numerically
  if (aMatch && bMatch) {
    const aNumber = parseInt(aMatch[1], 10);
    const bNumber = parseInt(bMatch[1], 10);
    return aNumber - bNumber;
  }

  // If only one string ends with a number, prioritize the one without
  if (aMatch && !bMatch) return 1;
  if (!aMatch && bMatch) return -1;

  // If neither string ends with a number, compare alphabetically
  return a.localeCompare(b);
};
